import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Save } from "lucide-react";
import { Label } from "@/components/ui/label";

function TagInput({ tags, setTags }) {
  const [input, setInput] = useState("");
  
  const addTag = () => {
    if (input.trim() && !tags.includes(input.trim())) {
      setTags([...tags, input.trim()]);
      setInput("");
    }
  };
  
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag, idx) => (
        <Badge key={idx} className="flex items-center gap-1">
          {tag.toUpperCase()}
          <X className="w-3 h-3 cursor-pointer" onClick={() => setTags(tags.filter((_, i) => i !== idx))} />
        </Badge>
      ))}
      <Input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if ((e.key === "Enter" || e.key === ",") && input.trim()) {
            addTag();
            e.preventDefault();
          }
        }}
        onBlur={addTag}
        placeholder="Add tag"
        className="w-24"
      />
    </div>
  );
}

function MedicineRow({ med, onChange, onRemove }) {
  return (
    <div className="flex gap-2 mb-2">
      <Input 
        id={`medicine-brand-${med.id || Date.now()}`}
        name="medicine-brand"
        value={med.brand_name || med.product || ""} 
        onChange={e => onChange({ ...med, brand_name: e.target.value, product: e.target.value })} 
        placeholder="Medicine" 
      />
      <Input 
        id={`medicine-dosage-${med.id || Date.now()}`}
        name="medicine-dosage"
        value={med.dosage} 
        onChange={e => onChange({ ...med, dosage: e.target.value })} 
        placeholder="Dosage" 
      />
      <Input 
        id={`medicine-when-${med.id || Date.now()}`}
        name="medicine-when"
        value={med.taken_when ?? med.when ?? ""} 
        onChange={e => onChange({ ...med, taken_when: e.target.value })} 
        placeholder="Timing" 
      />
      <Input 
        id={`medicine-duration-${med.id || Date.now()}`}
        name="medicine-duration"
        value={med.duration} 
        onChange={e => onChange({ ...med, duration: e.target.value })} 
        placeholder="Duration" 
      />
      <Input 
        id={`medicine-notes-${med.id || Date.now()}`}
        name="medicine-notes"
        value={med.notes} 
        onChange={e => onChange({ ...med, notes: e.target.value })} 
        placeholder="Notes" 
      />
      <Button variant="ghost" onClick={onRemove}><X className="w-4 h-4" /></Button>
    </div>
  );
}

function ComplaintRow({ row, onChange, onRemove }) {
  return (
    <div className="flex gap-2 mb-2">
      <Input 
        id={`complaint-text-${row.id || Date.now()}`}
        name="complaint-text"
        value={row.complaint} 
        onChange={e => onChange({ ...row, complaint: e.target.value })} 
        placeholder="Complaint" 
      />
      <Input 
        id={`complaint-frequency-${row.id || Date.now()}`}
        name="complaint-frequency"
        value={row.frequency} 
        onChange={e => onChange({ ...row, frequency: e.target.value })} 
        placeholder="Frequency" 
      />
      <Input 
        id={`complaint-severity-${row.id || Date.now()}`}
        name="complaint-severity"
        value={row.severity} 
        onChange={e => onChange({ ...row, severity: e.target.value })} 
        placeholder="Severity" 
      />
      <Input 
        id={`complaint-duration-${row.id || Date.now()}`}
        name="complaint-duration"
        value={row.duration} 
        onChange={e => onChange({ ...row, duration: e.target.value })} 
        placeholder="Duration" 
      />
      <Input 
        id={`complaint-date-${row.id || Date.now()}`}
        name="complaint-date"
        value={row.date} 
        onChange={e => onChange({ ...row, date: e.target.value })} 
        placeholder="Date" 
      />
      <Button variant="ghost" onClick={onRemove}><X className="w-4 h-4" /></Button>
    </div>
  );
}

function InvestigationRow({ row, onChange, onRemove }) {
  return (
    <div className="flex gap-2 mb-2">
      <Input 
        id={`investigation-name-${row.id || Date.now()}`}
        name="investigation-name"
        value={row.investigation} 
        onChange={e => onChange({ ...row, investigation: e.target.value })} 
        placeholder="Investigation" 
      />
      <Input 
        id={`investigation-notes-${row.id || Date.now()}`}
        name="investigation-notes"
        value={row.notes} 
        onChange={e => onChange({ ...row, notes: e.target.value })} 
        placeholder="Notes" 
      />
      <Button variant="ghost" onClick={onRemove}><X className="w-4 h-4" /></Button>
    </div>
  );
}

export function TemplateBuilderModal({ open, onClose, onSave, initialData, loading }) {
  const [type, setType] = useState(initialData?.type || "medicine");
  const [title, setTitle] = useState(initialData?.title || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [category, setCategory] = useState(initialData?.category || "personal");
  const [tags, setTags] = useState(initialData?.tags || []);
  const [medicines, setMedicines] = useState(initialData?.type === "medicine" ? initialData.value || [] : []);
  const [complaints, setComplaints] = useState(initialData?.type === "complaint" ? initialData.value || [] : []);
  const [investigations, setInvestigations] = useState(initialData?.type === "investigation" ? initialData.value || [] : []);
  const [richText, setRichText] = useState(
      ["advice", "diagnosis", "quick_notes"].includes(initialData?.type)
          ? (typeof initialData?.value === "string"
              ? initialData.value
              : initialData?.value?.value || "")
          : ""
  );

  useEffect(() => {
    setType(initialData?.type || "medicine");
    setTitle(initialData?.title || "");
    setDescription(initialData?.description || "");
    setCategory(initialData?.category || "personal");
    setTags(initialData?.tags || []);
    setMedicines(initialData?.type === "medicine" ? initialData.value || [] : []);
    setComplaints(initialData?.type === "complaint" ? initialData.value || [] : []);
    setInvestigations(initialData?.type === "investigation" ? initialData.value || [] : []);
    setRichText(
        ["advice", "diagnosis", "quick_notes"].includes(initialData?.type)
            ? (typeof initialData?.value === "string"
                ? initialData.value
                : initialData?.value?.value || "")
            : ""
    );  }, [open, initialData]);


  useEffect(() => {
    if (type === "medicine") {
      setComplaints([]);
      setInvestigations([]);
      setRichText("");
    } else if (type === "complaint") {
      setMedicines([]);
      setInvestigations([]);
      setRichText("");
    } else if (type === "investigation") {
      setMedicines([]);
      setComplaints([]);
      setRichText("");
    } else if (["advice", "diagnosis", "quick_notes"].includes(type)) {
      setMedicines([]);
      setComplaints([]);
      setInvestigations([]);
    }
  }, [type]);

  let value;
  if (type === "medicine") value = medicines;
  else if (type === "complaint") value = complaints;
  else if (type === "investigation") value = investigations;
  else if (["advice", "diagnosis", "quick_notes"].includes(type)) value = { value: richText.trim() };
  else value = richText;

  const isValid =
    title.trim() &&
    ((type === "medicine" && medicines.length > 0 && medicines.every(m => m.brand_name || m.product)) ||
      (type === "complaint" && complaints.length > 0 && complaints.every(c => c.complaint)) ||
      (type === "investigation" && investigations.length > 0 && investigations.every(i => i.investigation)) ||
      (["advice", "diagnosis", "quick_notes"].includes(type) && richText.trim()));

  const preview = () => {
    if (type === "medicine") {
      return (
        <ul>
          {medicines.map((m, i) => (
            <li key={i}>
              <b>{m.brand_name || m.product}</b> {m.dosage} {m.taken_when ?? m.when} {m.duration} {m.notes && `(${m.notes})`}
            </li>
          ))}
        </ul>
      );
    }
    if (type === "complaint") {
      return (
        <ul>
          {complaints.map((c, i) => (
            <li key={i}>
              <b>{c.complaint}</b> {c.frequency && `• ${c.frequency}`} {c.severity && `• ${c.severity}`} {c.duration && `• ${c.duration}`} {c.date && `• ${c.date}`}
            </li>
          ))}
        </ul>
      );
    }
    if (type === "investigation") {
      return (
        <ul>
          {investigations.map((i, idx) => (
            <li key={idx}>
              <b>{i.investigation}</b> {i.notes && `(${i.notes})`}
            </li>
          ))}
        </ul>
      );
    }
    return <div>{richText}</div>;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl flex flex-col h-[80vh]">
        <DialogHeader>
          <DialogTitle>Template Builder</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 flex-1 overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input 
                id="template-title"
                name="template-title"
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                placeholder="Enter template name..." 
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medicine">💊 Medicine</SelectItem>
                  <SelectItem value="complaint">🗣️ Complaint</SelectItem>
                  <SelectItem value="investigation">🔬 Investigation</SelectItem>
                  <SelectItem value="advice">📋 Advice</SelectItem>
                  <SelectItem value="diagnosis">🩺 Diagnosis</SelectItem>
                  <SelectItem value="quick_notes">📝 Quick Notes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input 
              id="template-description"
              name="template-description"
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              placeholder="Brief description..." 
            />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Personal</SelectItem>
                <SelectItem value="clinic">Clinic</SelectItem>
                <SelectItem value="shared">Shared</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Tags</Label>
            <TagInput tags={tags} setTags={setTags} />
          </div>
          {/* Dynamic fields */}
          {type === "medicine" && (
            <div>
              <Label>Medicines *</Label>
              {medicines.map((med, idx) => (
                <MedicineRow
                  key={idx}
                  med={med}
                  onChange={m => setMedicines(medicines.map((mm, i) => (i === idx ? m : mm)))}
                  onRemove={() => setMedicines(medicines.filter((_, i) => i !== idx))}
                />
              ))}
              <Button variant="outline" onClick={() => setMedicines([...medicines, { brand_name: "", product: "", dosage: "", taken_when: "", duration: "", notes: "" }])}>
                <Plus className="w-4 h-4 mr-1" /> Add Medicine
              </Button>
            </div>
          )}
          {type === "complaint" && (
            <div>
              <Label>Complaints *</Label>
              {complaints.map((row, idx) => (
                <ComplaintRow
                  key={idx}
                  row={row}
                  onChange={r => setComplaints(complaints.map((rr, i) => (i === idx ? r : rr)))}
                  onRemove={() => setComplaints(complaints.filter((_, i) => i !== idx))}
                />
              ))}
              <Button variant="outline" onClick={() => setComplaints([...complaints, { complaint: "", frequency: "", severity: "", duration: "", date: "" }])}>
                <Plus className="w-4 h-4 mr-1" /> Add Complaint
              </Button>
            </div>
          )}
          {type === "investigation" && (
            <div>
              <Label>Investigations *</Label>
              {investigations.map((row, idx) => (
                <InvestigationRow
                  key={idx}
                  row={row}
                  onChange={r => setInvestigations(investigations.map((rr, i) => (i === idx ? r : rr)))}
                  onRemove={() => setInvestigations(investigations.filter((_, i) => i !== idx))}
                />
              ))}
              <Button variant="outline" onClick={() => setInvestigations([...investigations, { investigation: "", notes: "" }])}>
                <Plus className="w-4 h-4 mr-1" /> Add Investigation
              </Button>
            </div>
          )}
          {["advice", "diagnosis", "quick_notes"].includes(type) && (
            <div>
              <Label>Content *</Label>
              <Textarea rows={6} value={richText} onChange={e => setRichText(e.target.value)} placeholder="Enter content..." />
            </div>
          )}
          {/* Live preview */}
          <div>
            <Label>Live Preview</Label>
            <div className="bg-muted p-3 rounded border mt-1">{preview()}</div>
          </div>
        </div>
        <DialogFooter className="bg-background z-10 border-t mt-2 px-0 py-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ title, description, type, category, tags, value })} disabled={!isValid || loading}>
            {loading ? <Save className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 